/**
 * Client Request ▸ Request Registration — the module's LANDING LIST
 * (design v2.0 §13, standing rule 10: a nav click opens the list, never a
 * blank detail).
 *
 * List → (New Request / open a row) → full-page detail is the only flow. The
 * detail is `ClientRequestDetailPage`, rendered in place of this list rather
 * than in a popup (standing rule 12).
 *
 * COLUMNS (prototype v21 `COLDEFS`): Date Time · Request ID · Client · Project ·
 * Services · Next appointment · Location · Owner · Status, plus the row Open
 * affordance. Date Time and Request ID are LOCKED — they identify the row, so
 * a user cannot hide them and be left looking at anonymous data. Location is
 * off by default, matching the prototype's default view.
 *
 * TOOLBAR — the shared `<AppToolbar>` in canonical order (standing rule 7),
 * with the prototype's three controls folded in rather than replacing it:
 * Columns goes in the toolbar's `left` slot, New is relabelled "New Request",
 * and the Audit log is the automatic config-gated chip on `<FormTitle>`.
 * Edit / Delete / Refresh / Export to are retained per §18.6.
 *
 * Everything else is the shared kit: `<AppDataGrid>` owns styling, per-column
 * filters, sort, pagination and the footer; `useDeleteFlow` owns the delete
 * confirm + reason gate. This page sets no fonts, grid styling or pagination
 * of its own (standing rules 7 and 9).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import './clientRequest.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useDeleteFlow } from '../../components/confirm';
import { useNavActions } from '../../hooks/useNavActions';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { consumePendingRecord } from '../../layouts/navigateTo';
import ClientRequestDetailPage from './ClientRequestDetailPage';
import {
  AUDIT_FORM_NAME, fetchCategories,
  type ClientRequestRow, type ServiceCategory,
} from './clientRequestApi';

/** The optional columns, in display order. Date Time and Request ID are absent
 *  deliberately — they are LOCKED and always render (prototype `LOCKED_COLS`). */
const OPTIONAL_COLS = [
  ['client',   'Client'],
  ['project',  'Project'],
  ['services', 'Services'],
  ['next',     'Next appointment'],
  ['location', 'Location'],
  ['owner',    'Owner'],
  ['status',   'Status'],
] as const;

type OptionalColKey = typeof OPTIONAL_COLS[number][0];

/** Default visibility — everything except Location, matching the prototype's
 *  default view (it is available in the Columns chooser). */
const DEFAULT_COLS: Record<OptionalColKey, boolean> = {
  client: true, project: true, services: true,
  next: true, location: false, owner: true, status: true,
};

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Status → the shared pill tone. Status is the only column coloured by the
 *  grid itself; the Services chips are the documented domain exception. */
function statusTone(s?: string | null): string {
  switch (s) {
    case 'Completed':   return 'ok';
    case 'In Progress': return 'info';
    case 'Scheduled':   return 'info';
    case 'Submitted':   return 'warn';
    default:            return 'mute';   // Draft
  }
}

/** "09/02/2026 09:14" — always through the shared helper (§14b.6): local
 *  timezone, 24-hour, never AM/PM, never formatted inline. */
function fmtDateTime(v?: string | null): string {
  return v ? (fmtLocalDateTime(v) || '—') : '—';
}

/** The weekday for the Date Time cell's muted suffix. Derived from the same
 *  instant the cell shows, so the two can never disagree. */
function dowOf(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : (DOW[d.getDay()] ?? '');
}

/** "09/04/2026 · 09:00" — the appointment date and, when set, its time.
 *  The date goes through the shared helper; the time is already a plain
 *  wall-clock "HH:mm:ss" from SQL, so it is trimmed, not re-parsed (re-parsing
 *  a bare time as a date is what produces AM/PM bugs). */
function fmtAppointment(date?: string | null, time?: string | null): string {
  if (!date) return '—';
  const d = fmtLocalDate(date) || '—';
  const t = time ? time.slice(0, 5) : '';
  return t ? `${d} · ${t}` : d;
}

export default function RequestRegistrationPage() {
  const [rows,    setRows]    = useState<ClientRequestRow[]>([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMs,  setLoadMs]  = useState<number | null>(null);
  const [error,   setError]   = useState<string>();
  const [sel,     setSel]     = useState<Set<string | number>>(new Set());

  // Filters — the prototype's search + All categories + All statuses.
  const [search,     setSearch]     = useState('');
  const [category,   setCategory]   = useState('');
  const [statusMode, setStatusMode] = useState('');

  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [cols,       setCols]       = useState<Record<OptionalColKey, boolean>>(DEFAULT_COLS);
  const [colsOpen,   setColsOpen]   = useState(false);

  // null = the list is showing. A string id (or 'new') swaps in the full-page
  // detail — the list is REPLACED, not covered by a popup (standing rule 12).
  //
  // It starts non-null when another form sent the user HERE to see one request
  // ("Open request" on a Service Tracking record). That is the select-a-row step
  // of List → select → detail performed from elsewhere, so landing on the list
  // would lose the record they clicked. The handoff is CONSUMED, so arriving
  // from the sidebar still opens the list (standing rule 10).
  const [editing, setEditing] = useState<string | null>(
    () => consumePendingRecord('reqreg'));

  const deleteFlow = useDeleteFlow();
  // Role-Based MAC — hide New/Edit/Delete for a read-only role (reqreg →
  // create/write/delete), same pattern as Task Calendar. Falls open for admins
  // and the Service account. (owner feedback 2026-10-23)
  const perm = useNavActions('reqreg');
  const abortRef   = useRef<AbortController>();

  const loadData = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(undefined);
    const t0 = performance.now();
    try {
      // take=500 is the SP's own cap: AppDataGrid pages client-side, so one
      // round trip serves the working set. Server-side paging becomes
      // worthwhile when a lab passes that many live requests.
      const res = await api.get<{ success: boolean; data: ClientRequestRow[]; meta?: { total: number } }>(
        '/api/v1/client-requests', { params: { take: 500 }, signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      if (res.data.success) {
        setRows(res.data.data ?? []);
        setTotal(res.data.meta?.total ?? res.data.data?.length ?? 0);
      } else {
        setError('Failed to load client requests.');
      }
    } catch (e: unknown) {
      if (!ctrl.signal.aborted) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        if (msg !== 'canceled' && msg !== 'CanceledError') setError(msg);
      }
    } finally {
      if (!ctrl.signal.aborted) {
        setLoadMs(performance.now() - t0);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // The category vocabulary (chip labels + tones, and the filter's options).
  // Fetched rather than hardcoded so it has ONE home — and so it keeps working
  // unchanged when AI_ServiceDefinition becomes its source (design §5).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await fetchCategories();
        if (alive) setCategories(c);
      } catch { /* chips fall back to the raw code; the filter just stays empty */ }
    })();
    return () => { alive = false; };
  }, []);

  // A changed row set invalidates a selection that pointed into the old one.
  useEffect(() => { setSel(new Set()); }, [rows, search, category, statusMode]);
  // Close the Columns popup on any outside click.
  useEffect(() => {
    if (!colsOpen) return;
    const close = () => setColsOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [colsOpen]);

  const catByCode = useMemo(
    () => new Map(categories.map(c => [c.code, c] as const)), [categories]);

  /** The category chips for one row, resolved from the CSV of codes. */
  const chipsOf = useCallback((r: ClientRequestRow) => {
    const codes = (r.services ?? '').split(',').map(s => s.trim()).filter(Boolean);
    return codes.map(code => catByCode.get(code)
      ?? { code, name: code, tone: 'neutral' });   // unknown code still shows, greyed
  }, [catByCode]);

  /** Plain-text Services, for search, sort and export — the chips are visual
   *  only, so every non-visual consumer needs the same value as words. */
  const servicesText = useCallback(
    (r: ClientRequestRow) => chipsOf(r).map(c => c.name).join(', '), [chipsOf]);

  /** Search · category · status, applied client-side over the loaded page.
   *  The SP supports all three server-side too (for when paging moves to the
   *  server); doing it here keeps typing instant. */
  const filtered = useMemo(() => {
    let out = rows;

    if (category) {
      out = out.filter(r => (r.services ?? '').split(',').includes(category));
    }
    if (statusMode === 'open') out = out.filter(r => r.status !== 'Completed');
    else if (statusMode === 'done') out = out.filter(r => r.status === 'Completed');

    const s = search.toLowerCase().trim();
    if (s) {
      out = out.filter(r =>
        [r.requestNo, r.clientName, r.projectName, r.projectCode,
         servicesText(r), r.ownerName, r.status, r.location]
          .some(v => v && String(v).toLowerCase().includes(s)));
    }
    return out;
  }, [rows, search, category, statusMode, servicesText]);

  const selectedId = sel.size === 1 ? String([...sel][0]) : null;

  /** The columns, in the prototype's order, filtered by the Columns chooser.
   *  Date Time and Request ID are always present (locked). */
  const gridCols: AppColumn<ClientRequestRow>[] = useMemo(() => {
    const all: (AppColumn<ClientRequestRow> & { _key?: OptionalColKey })[] = [
      // ── Locked ──────────────────────────────────────────────────────────
      { field: 'createdAt', header: 'Date Time', width: 165,
        text: r => fmtDateTime(r.createdAt),
        render: r => (
          <span>
            {fmtDateTime(r.createdAt)}
            <span className="cr-dow">{dowOf(r.createdAt)}</span>
          </span>
        ) },
      { field: 'requestNo', header: 'Request ID', width: 120,
        text: r => r.requestNo ?? '',
        render: r => r.requestNo
          ? <span>{r.requestNo}</span>
          : <span className="na">On save</span> },

      // ── Optional ────────────────────────────────────────────────────────
      { _key: 'client',  field: 'clientName',  header: 'Client',  width: 190 },
      { _key: 'project', field: 'projectName', header: 'Project', width: 175 },
      { _key: 'services', field: 'services', header: 'Services', width: 170,
        // Sort and filter on the readable label, not the raw code CSV.
        text: r => servicesText(r),
        render: r => {
          const chips = chipsOf(r);
          if (!chips.length) return <span className="na">—</span>;
          return (
            <span className="cr-chips">
              {chips.map(c => (
                <span key={c.code} className={`cr-chip ${c.tone}`}>{c.name}</span>
              ))}
            </span>
          );
        } },
      { _key: 'next', field: 'nextAppointmentDate', header: 'Next appointment', width: 165,
        text: r => fmtAppointment(r.nextAppointmentDate, r.nextAppointmentTime),
        render: r => {
          const v = fmtAppointment(r.nextAppointmentDate, r.nextAppointmentTime);
          return v === '—' ? <span className="na">—</span> : <span>{v}</span>;
        } },
      { _key: 'location', field: 'location', header: 'Location', width: 180,
        // The prototype shows only the first address segment — the street, not
        // the whole postal address, which would swamp the column.
        text: r => (r.location ?? '').split(',')[0] ?? '',
        render: r => r.location
          ? <span>{r.location.split(',')[0]}</span>
          : <span className="na">—</span> },
      { _key: 'owner', field: 'ownerName', header: 'Owner', width: 140,
        text: r => r.ownerName ?? '',
        render: r => r.ownerName
          ? <span>{r.ownerName}</span>
          : <span className="na">Unassigned</span> },
      { _key: 'status', field: 'status', header: 'Status', width: 125,
        text: r => r.status ?? '',
        render: r => <span className={`bl-pill ${statusTone(r.status)}`}>{r.status || '—'}</span> },

      // ── The row affordance ──────────────────────────────────────────────
      { field: 'id', header: '', width: 70, noExport: true,
        text: () => '',
        render: () => <span className="cr-open">Open</span> },
    ];

    return all
      .filter(c => !c._key || cols[c._key])
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- _key is named only to STRIP it from the column, which is what the rest-spread does
      .map(({ _key, ...col }) => col);
  }, [cols, chipsOf, servicesText]);

  /** Delete → shared danger confirm + reason gate → soft delete → "Deleted." */
  const onDelete = useCallback(async () => {
    if (!selectedId) return;
    const row = rows.find(r => r.id === selectedId);
    await deleteFlow({
      formName: AUDIT_FORM_NAME,
      message: `Delete client request ${row?.requestNo ?? ''}?`,
      delete: async () => { await api.delete(`/api/v1/client-requests/${selectedId}`); },
      onDeleted: () => { setSel(new Set()); loadData(); },
    });
  }, [selectedId, rows, deleteFlow, loadData]);

  // ── Full-page detail replaces the list (standing rule 12) ─────────────────
  if (editing !== null) {
    return (
      <ClientRequestDetailPage
        requestId={editing === 'new' ? null : editing}
        // The ids of what the user is actually looking at, so the detail's
        // prev/next arrows walk that exact set.
        siblingIds={filtered.map(r => r.id)}
        onClose={() => { setEditing(null); loadData(); }}
        onNavigate={setEditing}
      />
    );
  }

  return (
    <div className="bl-page">

      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Client Request</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Request Registration</span>
          </div>
          {/* Shared FormTitle — nav icon/colour via navMeta, the ONE top
              load-time chip, and the config-gated Audit log chip. */}
          <div className="dp-title-row">
            <FormTitle pageId="reqreg" auditFormName={AUDIT_FORM_NAME}
                       loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            Register and manage client requests across all service categories.
            {' · '}{loading ? '…' : `${total} request${total === 1 ? '' : 's'}`}
          </div>
        </div>

        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle"
               style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
            {error}
          </div>
        )}
      </div>

      <div className="bl-grid-area">
        {/* Shared toolbar, canonical order (standing rule 7). The prototype's
            Columns button rides in the `left` slot; its Audit button is the
            automatic chip on FormTitle above; its "New Request" is New with a
            label. Edit / Delete / Refresh / Export to are retained per §18.6. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <span className="cr-colswrap" onClick={e => e.stopPropagation()}>
                <button className="ca-btn" title="Choose which columns are shown"
                        onClick={() => setColsOpen(o => !o)}>
                  <span className="tb-i tb-i-blue"><i className="ti ti-columns-3" /></span> Columns
                </button>
                {colsOpen && (
                  <div className="cr-cols">
                    <div className="cr-cols-h">Columns</div>
                    <div className="cr-cols-l">
                      {/* The locked pair is listed too, so the chooser shows the
                          COMPLETE column set and makes clear they are fixed. */}
                      <label className="cr-cols-o locked">
                        <input type="checkbox" checked disabled readOnly />
                        <span>Date Time</span>
                      </label>
                      <label className="cr-cols-o locked">
                        <input type="checkbox" checked disabled readOnly />
                        <span>Request ID</span>
                      </label>
                      {OPTIONAL_COLS.map(([key, label]) => (
                        <label key={key} className="cr-cols-o">
                          <input
                            type="checkbox"
                            checked={cols[key]}
                            onChange={() => setCols(c => ({ ...c, [key]: !c[key] }))}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </span>
            }
            onNew={perm.create ? () => setEditing('new') : undefined}
            newLabel="New"
            onEdit={perm.write ? () => selectedId && setEditing(selectedId) : undefined}
            editDisabled={!selectedId}
            onDelete={perm.delete ? onDelete : undefined}
            deleteDisabled={!selectedId}
            onRefresh={loadData}
            refreshing={loading}
            refreshDisabled={loading}
            exportData={{ ...gridExportMatrix(filtered, gridCols), filename: 'client-requests' }}
          />
        </div>

        {/* Search · All categories · All statuses — the prototype's filter row.
            `cr-filters` lets the search box stretch to fill the row (see the CSS
            for why that override is scoped here rather than shared). */}
        <div className="list-search-row cr-filters">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              placeholder="Search Request ID, client, project, or service"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>

          <select className="cr-fsel" value={category}
                  onChange={e => setCategory(e.target.value)}
                  aria-label="Filter by service category">
            <option value="">All categories</option>
            {categories.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>

          <select className="cr-fsel" value={statusMode}
                  onChange={e => setStatusMode(e.target.value)}
                  aria-label="Filter by status">
            <option value="">All status</option>
            <option value="open">Active requests</option>
            <option value="done">Completed</option>
          </select>

          {filtered.length !== rows.length && (
            <span className="search-match-count">
              {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>

        <div className="bl-card bl-card-flex">
          <AppDataGrid<ClientRequestRow>
            rows={filtered}
            columns={gridCols}
            getRowId={r => r.id}
            loading={loading}
            loadMs={loadMs}
            // 20 for THIS form (owner request 2026-09-08), not a change to the
            // shared default: AppDataGrid's app-wide default is 100 by the
            // 2026-07-02 GoLive-parity decision, and moving it would repage
            // every grid in the app. The dropdown still offers the full set.
            defaultPageSize={20}
            selectable
            selectedIds={sel}
            onSelectionChange={setSel}
            // Open a row → its detail. List → select → detail (rule 10).
            onRowClick={r => setEditing(r.id)}
            emptyText="No client requests yet — use New Request to register one."
          />
        </div>
      </div>
    </div>
  );
}
